/**
* @file 协议中心-协议管理
* */
<template>
  <div id="protocolManage">
    <!-- 工具栏 -->
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增协议</el-button>
    </div>

    <!-- 协议列表（按端口分组） -->
    <div v-for="group in groupedList" :key="group.name" class="protocol-group">
      <div class="group-title">{{ group.name }}</div>
      <el-table :data="group.list" border size="small">
        <el-table-column prop="title" label="协议标题" width="800"></el-table-column>
        <el-table-column prop="usePage" label="端口类型" width="200">
          <template slot-scope="scope">
            <el-tag size="mini" :type="getPageTagType(scope.row)">{{ getPageLabel(scope.row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="版本" width="70" align="center">
          <template slot-scope="scope">
            <span>v{{ scope.row.version || 1 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="是否启用" width="100" align="center">
          <template slot-scope="scope">
            <el-switch v-model="scope.row.status" :active-value="1" :inactive-value="2"
                       @change="handleStatusChange($event, scope.row)"></el-switch>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" width="160">
          <template slot-scope="scope">{{ scope.row.updated | formatTime }}</template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" min-width="130">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="small" @click="handleView(scope.row)">预览</el-button>
            <el-button v-if="scope.row.allowCustom === 1" type="text" size="small" style="color:#F56C6C"
                       @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 新增/编辑 Dialog -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="820px" append-to-body>
      <el-form ref="form" :model="form" :rules="rules" label-width="100px" size="small">
        <el-form-item label="协议标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入协议标题" maxlength="20" show-word-limit></el-input>
        </el-form-item>
        <el-form-item label="端口类型" prop="usePage">
          <el-select v-model="form.usePage" placeholder="请选择端口类型" style="width:100%" :disabled="!isCustom">
            <el-option v-for="item in pageTypeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item v-if="isCustom" label="自定义类型" prop="customType">
          <el-input v-model="form.customType" placeholder="自定义端口名称（如 web/miniapp）" maxlength="50"></el-input>
        </el-form-item>
        <el-form-item label="协议概述">
          <wangeditor5 :showToolbar="true" :content.sync="form.summary" :height="150"></wangeditor5>
        </el-form-item>
        <el-form-item label="协议全文" prop="content">
          <div class="editor-toolbar-extra">
            <span class="editor-tips">支持富文本编辑，可粘贴 Markdown 内容自动转换</span>
            <el-button size="mini" icon="el-icon-upload2" @click="triggerMdImport">导入 Markdown 文件</el-button>
            <input ref="mdFileInput" type="file" accept=".md,.markdown,.txt" style="display:none"
                   @change="handleMdFileImport" />
          </div>
          <wangeditor5 ref="contentEditor" :content.sync="form.content" styleObject="height: 360px"></wangeditor5>
        </el-form-item>
        <el-form-item label="是否启用">
          <el-switch v-model="form.status" :active-value="1" :inactive-value="2"></el-switch>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" placeholder="备注信息" maxlength="30"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" icon="el-icon-view" @click="handlePreviewCurrent">预览</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="handleSave">保存</el-button>
      </span>
    </el-dialog>

    <!-- 预览 Dialog -->
    <el-dialog title="协议预览" :visible.sync="previewVisible" width="860px" append-to-body>
      <div class="preview-title">{{ previewData.title }}</div>
      <div style="font-size:12px;color:#909399;margin-bottom:12px">
        版本：v{{ previewData.version || 1 }} &nbsp;|&nbsp;
        状态：{{ previewData.status === 1 ? '已启用' : '已禁用' }}
      </div>
      <el-tabs v-model="previewTab" size="small">
        <el-tab-pane label="渲染预览" name="render">
          <div class="preview-scroll">
            <markdown-viewer v-if="isMarkdownContent(previewData.content)" :content="previewData.content"></markdown-viewer>
            <div v-else class="preview-html-content" v-html="previewData.content"></div>
          </div>
        </el-tab-pane>
        <el-tab-pane label="概述预览" name="summary">
          <div class="preview-scroll">
            <markdown-viewer v-if="isMarkdownContent(previewData.summary)" :content="previewData.summary"></markdown-viewer>
            <div v-else v-html="previewData.summary || '<p style=\'color:#909399\'>（无概述）</p>'"></div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>
  </div>
</template>

<script>
import wangeditor5 from "@/components/wangeditor5.vue";
import MarkdownViewer from "@/components/markdownViewer.vue";
import { marked } from 'marked';

const PAGE_TYPE_OPTIONS = [
  { label: '乘客端 - 隐私政策', value: 1, group: '乘客端' },
  { label: '乘客端 - 乘车须知', value: 2, group: '乘客端' },
  { label: '乘客端 - 平台服务协议', value: 13, group: '乘客端' },
  { label: '乘客端 - 车辆租赁协议', value: 14, group: '乘客端' },
  { label: '乘客端 - 代驾服务协议', value: 15, group: '乘客端' },
  { label: '乘客端 - 用户协议', value: 18, group: '乘客端' },
  { label: '司机端 - 隐私政策', value: 3, group: '司机端' },
  { label: '司机端 - 服务标准协议', value: 5, group: '司机端' },
  { label: '司机端 - 车主认证常见问题', value: 6, group: '司机端' },
  { label: '司机端 - 违约责任认定', value: 7, group: '司机端' },
  { label: '司机端 - 认证常见问题', value: 8, group: '司机端' },
  { label: '司机端 - 用户协议', value: 16, group: '司机端' },
  { label: '司机端 - 提现须知', value: 17, group: '司机端' },
  { label: '司机端 - 隐私政策弹窗', value: 19, group: '司机端' },
  { label: '移动售票端 - 隐私政策', value: 4, group: '移动售票端' },
  { label: '渠道端 - 用户协议弹窗', value: 20, group: '渠道端' },
  { label: '渠道端 - 隐私政策弹窗', value: 21, group: '渠道端' },
  { label: '自定义协议', value: 99, group: '自定义' },
];

export default {
  name: "protocolManage",
  components: { wangeditor5, MarkdownViewer },
  data() {
    return {
      tableData: [],
      dialogVisible: false,
      previewVisible: false,
      saving: false,
      isCustom: false,
      form: { title: '', usePage: null, customType: '', summary: '', content: '', status: 1, remark: '', allowCustom: 0 },
      previewData: {},
      previewTab: 'render',
      rules: {
        title: [{ required: true, message: '请输入协议标题', trigger: 'blur' }],
        usePage: [{ required: true, message: '请选择端口类型', trigger: 'change' }],
        content: [{ required: true, message: '请输入协议内容', trigger: 'blur' }],
      },
      pageTypeOptions: PAGE_TYPE_OPTIONS,
    };
  },
  computed: {
    dialogTitle() {
      return this.form.id ? '编辑协议' : '新增协议';
    },
    groupedList() {
      const groupMap = {};
      this.tableData.forEach(item => {
        const opt = PAGE_TYPE_OPTIONS.find(o => o.value === item.usePage);
        const groupName = item.allowCustom === 1
            ? ('自定义 - ' + (item.customType || '其他'))
            : (opt ? opt.group : '其他');
        if (!groupMap[groupName]) groupMap[groupName] = { name: groupName, list: [] };
        groupMap[groupName].list.push(item);
      });
      return Object.values(groupMap);
    }
  },
  created() {
    this.getList();
  },
  methods: {
    async getList() {
      const res = await this.$axios.get('/api/v1/manage/protocol/list', { appKey: this.mixinUserInfo.appKey });
      if (res && res.code === 1) this.tableData = res.data || [];
    },
    handleAdd() {
      this.isCustom = false;
      this.form = { title: '', usePage: null, customType: '', summary: '', content: '', status: 1, remark: '', allowCustom: 0 };
      this.dialogVisible = true;
    },
    handleEdit(row) {
      this.isCustom = row.allowCustom === 1;
      this.form = { ...row };
      this.dialogVisible = true;
    },
    handleView(row) {
      this.previewData = row;
      this.previewTab = 'render';
      this.previewVisible = true;
    },
    handlePreviewCurrent() {
      this.previewData = { ...this.form };
      this.previewTab = 'render';
      this.previewVisible = true;
    },
    triggerMdImport() {
      this.$refs.mdFileInput.value = '';
      this.$refs.mdFileInput.click();
    },
    handleMdFileImport(e) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (evt) => {
        const mdContent = evt.target.result;
        const html = marked.parse(mdContent, { breaks: true, gfm: true });
        this.form.content = html;
        if (this.$refs.contentEditor && this.$refs.contentEditor.setMarkdownContent) {
          this.$refs.contentEditor.setMarkdownContent(mdContent);
        }
        this.$message.success('Markdown 文件导入成功，已转换为富文本');
      };
      reader.readAsText(file, 'UTF-8');
    },
    isMarkdownContent(content) {
      if (!content) return false;
      const mdPatterns = [/^#{1,6}\s+/m, /^[-*+]\s+/m, /^\d+\.\s+/m, /^```/m, /^>\s+/m, /\|.*\|/m];
      return mdPatterns.filter(p => p.test(content)).length >= 2;
    },
    async handleDelete(row) {
      await this.$confirm('确认删除该协议吗？', '删除提示', { type: 'warning' });
      const res = await this.$axios.delete(`/api/v1/manage/protocol/${row.id}`, { params: { appKey: this.mixinUserInfo.appKey } });
      if (res && res.code === 1) {
        this.$message.success('删除成功');
        this.getList();
      }
    },
    async handleStatusChange(status, row) {
      const res = await this.$axios.post('/api/v1/manage/protocol/status', {
        id: row.id, status, appKey: this.mixinUserInfo.appKey
      });
      if (res && res.code === 1) {
        this.$message.success('操作成功');
      } else {
        row.status = status === 1 ? 2 : 1;
        this.getList();
      }
    },
    async handleSave() {
      await this.$refs.form.validate();
      if (!this.form.content) { this.$message.info('请输入协议全文'); return; }
      // 自定义协议 usePage 使用 99
      const params = {
        ...this.form,
        appKey: this.mixinUserInfo.appKey,
        allowCustom: this.isCustom ? 1 : 0,
        usePage: this.isCustom ? 99 : this.form.usePage,
      };
      this.saving = true;
      const res = await this.$axios.post('/api/v1/manage/protocol', params);
      this.saving = false;
      if (res && res.code === 1) {
        this.$message.success('保存成功');
        this.dialogVisible = false;
        this.getList();
      }
    },
    getPageLabel(row) {
      if (row.allowCustom === 1) return '自定义 - ' + (row.customType || '其他');
      const opt = PAGE_TYPE_OPTIONS.find(o => o.value === row.usePage);
      return opt ? opt.label : ('type-' + row.usePage);
    },
    getPageTagType(row) {
      if (row.allowCustom === 1) return 'warning';
      const group = PAGE_TYPE_OPTIONS.find(o => o.value === row.usePage)?.group;
      if (group === '乘客端') return '';
      if (group === '司机端') return 'success';
      if (group === '移动售票端') return 'info';
      if (group === '渠道端') return 'danger';
      return 'info';
    },
  },
  filters: {
    formatTime(val) {
      if (!val) return '-';
      return new Date(val * 1000).toLocaleString('zh-CN', { hour12: false });
    }
  }
};
</script>

<style lang="less" scoped>
#protocolManage {
  padding: 16px;

  .toolbar {
    margin-bottom: 16px;
  }

  .protocol-group {
    margin-bottom: 24px;

    .group-title {
      font-size: 14px;
      font-weight: bold;
      color: #333;
      margin-bottom: 8px;
      padding-left: 8px;
      border-left: 3px solid #409EFF;
    }
  }

  .preview-title {
    font-size: 18px;
    font-weight: bold;
    text-align: center;
    margin-bottom: 12px;
  }

  .preview-summary {
    color: #606266;
    font-size: 13px;
    margin-bottom: 16px;
    padding: 8px;
    background: #f5f7fa;
    border-radius: 4px;
  }

  .preview-content {
    max-height: 500px;
    overflow-y: auto;
    font-size: 14px;
    line-height: 1.8;
  }
}
</style>
